<template>
	<component
		:is="componentType"
		:class="buttonClasses"
		:href="href"
		:target="target"
		:disabled="disabled"
		:aria-disabled="disabled"
		:type="type"
		tabindex="0"
	>
		<AppIcon
			v-if="variant === 'target' && !disabled"
			:name="IconName.Locate"
			class="target-icon"
			:size="12"
			:stroke-width="2"
		/>

		<slot></slot>

		<AppIcon
			v-if="variant === 'external' && !disabled"
			:name="IconName.ExternalLink"
			class="arrow"
			:size="12"
			:stroke-width="2"
		/>
	</component>
</template>

<script setup lang="ts">
import { computed } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

const props = defineProps({
	href: {
		type: String,
		required: false,
	},
	target: {
		type: String,
		default: "_self",
		validator(value: string) {
			return ["_self", "_blank", "_parent", "_top", "_unfencedTop"].includes(value)
		},
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	variant: {
		type: String,
		default: "external",
		validator(value: string) {
			return ["external", "primary", "secondary", "target", "tertiary", "danger", "ghost"].includes(value)
		},
	},
	size: {
		type: String,
		default: "l",
		validator(value: string) {
			return ["xs", "s", "m", "l"].includes(value)
		},
	},
	shape: {
		type: String,
		default: "rectangle",
		validator(value: string) {
			return ["rectangle", "pill"].includes(value)
		},
	},
	type: {
		type: String,
		default: "button",
		validator(value: string) {
			return ["button", "submit", "reset"].includes(value)
		},
	},
})

const componentType = computed(() => {
	return props.href ? "a" : "button"
})

const buttonClasses = computed(() => {
	return [
		"btn",
		`btn--${props.variant}`,
		`btn--${props.size}`,
		{
			"btn--disabled": props.disabled,
			"btn--pill": props.shape === "pill",
		},
	]
})
</script>

<style scoped>
/* base button */
.btn {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	font-family: var(--font-family-primary);
	font-weight: var(--font-weight-semibold);
	text-decoration: none;
	white-space: nowrap;

	border: var(--border-width-thin) solid;
	border-radius: var(--border-radius-base);

	cursor: pointer;
	transition: all 0.2s ease;

	/* shape override, takes precedence over the default rectangular corners */
	&.btn--pill {
		border-radius: var(--border-radius-full);
	}

	&:focus-visible {
		outline: 2px solid var(--primary-green);
		outline-offset: 2px;
	}
}

/* sizes */
.btn--xs {
	--btn-font-size: var(--font-size-small);

	padding: var(--spacing-xxs) var(--spacing-md);
	font-size: var(--btn-font-size);
	height: 28px;
}

.btn--s {
	--btn-font-size: var(--font-size-xs);

	padding: var(--spacing-xxs) var(--spacing-sm);
	font-size: var(--btn-font-size);
	min-width: 100px;
	height: 24px;
}

.btn--m {
	--btn-font-size: var(--font-size-small);

	padding: var(--spacing-xxs) var(--spacing-md);
	font-size: var(--btn-font-size);
	min-width: 160px;
	height: 28px;
}

.btn--l {
	--btn-font-size: var(--font-size-base);

	padding: var(--spacing-xs) var(--spacing-md);
	font-size: var(--btn-font-size);
	min-width: 160px;
	height: 36px;
}

/* variant external (green with arrow, for navigating to an external resource) */
.btn--external {
	gap: var(--spacing-xs);
	color: var(--neutral-0);
	background-color: var(--primary-green);
	border-color: var(--primary-green);
	overflow: hidden;
	font-size: var(--btn-font-size);

	.arrow {
		display: block;
		flex-shrink: 0;
		margin-top: -1px;
		transition: transform 0.2s ease-in-out;
	}

	&:hover:not(.btn--disabled) {
		background-color: color-mix(in srgb, var(--primary-green) 82%, black);
		border-color: color-mix(in srgb, var(--primary-green) 82%, black);

		.arrow {
			transform: translate(1px, -1px);
		}
	}
}

/* variant primary (solid green, main in-app action) */
.btn--primary {
	color: var(--neutral-0);
	background-color: var(--primary-green);
	border-color: var(--primary-green);

	&:hover:not(.btn--disabled) {
		background-color: color-mix(in srgb, var(--primary-green) 82%, black);
		border-color: color-mix(in srgb, var(--primary-green) 82%, black);
	}
}

/* variant secondary (outlined, black hover green) */
.btn--secondary {
	color: var(--neutral-900);
	background-color: var(--neutral-0);
	border-color: var(--neutral-400);

	&:hover:not(.btn--disabled) {
		color: var(--primary-green);
		border-color: var(--primary-green);
	}
}

/* variant target (solid red, white text/icon - for opportunity target actions) */
.btn--target {
	gap: var(--spacing-xs);
	color: var(--neutral-0);
	background-color: var(--accent-target);
	border-color: var(--accent-target);

	.target-icon {
		display: block;
		flex-shrink: 0;
		margin-top: -1px;
		color: var(--neutral-0);
		transition: transform 0.2s ease-in-out;
	}

	&:hover:not(.btn--disabled) {
		background-color: color-mix(in srgb, var(--accent-target) 82%, black);
		border-color: color-mix(in srgb, var(--accent-target) 82%, black);

		.target-icon {
			transform: scale(1.15);
		}
	}
}

/* variant tertiary (outlined, grey hover black) */
.btn--tertiary {
	color: var(--neutral-600);
	background-color: var(--neutral-0);
	border-color: var(--neutral-400);

	&:hover:not(.btn--disabled) {
		color: var(--neutral-900);
		border-color: var(--neutral-600);
	}
}

/* variant danger (destructive action) */
.btn--danger {
	color: var(--neutral-0);
	background-color: var(--accent-warning);
	border-color: var(--accent-warning);

	&:hover:not(.btn--disabled) {
		background-color: var(--neutral-0);
		color: var(--accent-warning);
	}
}

/* variant ghost (borderless link) */
.btn--ghost {
	min-width: 0;
	height: auto;
	padding: 0;
	border-color: transparent;
	background-color: transparent;
	color: var(--primary-green);

	&:hover:not(.btn--disabled) {
		text-decoration: underline;
	}
}

/* disabled */
.btn--disabled {
	opacity: 0.5;
	cursor: not-allowed;

	&.btn--external,
	&.btn--primary,
	&.btn--target {
		background-color: var(--neutral-200);
		color: var(--neutral-900);
		border-color: var(--neutral-600);
	}
}
</style>
